import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["viewport", "stage", "image", "markers", "editor", "name", "error", "status", "saveButton", "zoomLabel", "count"]
  static values = { url: String, items: Array }

  connect() {
    this.zoom = 1
    this.items = this.itemsValue
    this.reposition = () => this.positionEditor()
    this.viewportTarget.addEventListener("scroll", this.reposition)
    window.addEventListener("resize", this.reposition)
    this.render()
  }

  disconnect() {
    this.viewportTarget.removeEventListener("scroll", this.reposition)
    window.removeEventListener("resize", this.reposition)
  }

  zoomIn() { this.setZoom(this.zoom * 1.4) }
  zoomOut() { this.setZoom(this.zoom / 1.4) }
  resetZoom() { this.setZoom(1) }

  setZoom(value) {
    const viewport = this.viewportTarget
    const oldWidth = this.stageTarget.offsetWidth
    const centerX = (viewport.scrollLeft + viewport.clientWidth / 2) / oldWidth
    const centerY = (viewport.scrollTop + viewport.clientHeight / 2) / this.stageTarget.offsetHeight
    this.zoom = Math.min(6, Math.max(1, value))
    this.stageTarget.style.width = `${this.zoom * 100}%`
    viewport.scrollLeft = centerX * this.stageTarget.offsetWidth - viewport.clientWidth / 2
    viewport.scrollTop = centerY * this.stageTarget.offsetHeight - viewport.clientHeight / 2
    this.zoomLabelTarget.textContent = `${Math.round(this.zoom * 100)} %`
    this.positionEditor()
  }

  render() {
    this.markersTarget.replaceChildren()
    this.items.forEach(item => {
      const marker = document.createElement("button")
      marker.type = "button"
      marker.className = "photo-marker"
      marker.style.left = `${item.x * 100}%`
      marker.style.top = `${item.y * 100}%`
      marker.title = item.name
      marker.setAttribute("aria-label", `${item.name} bearbeiten`)
      marker.addEventListener("click", event => {
        event.stopPropagation()
        this.open(item)
      })
      this.markersTarget.append(marker)
    })
    this.countTarget.textContent = `${this.items.length} ${this.items.length === 1 ? "Person" : "Personen"} benannt`
  }

  place(event) {
    if (this.saving) return
    const rect = this.imageTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    this.open({ x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)), name: "" })
  }

  open(item) {
    if (this.saving) return
    this.selected = { ...item }
    this.nameTarget.value = item.name
    this.errorTarget.textContent = ""
    this.editorTarget.hidden = false
    this.positionEditor()
    this.nameTarget.focus({ preventScroll: true })
  }

  positionEditor() {
    if (!this.selected || this.editorTarget.hidden) return
    const image = this.imageTarget.getBoundingClientRect()
    const root = this.element.getBoundingClientRect()
    const viewport = this.viewportTarget.getBoundingClientRect()
    const editor = this.editorTarget
    const x = image.left + this.selected.x * image.width - root.left + 18
    const y = image.top + this.selected.y * image.height - root.top + 18
    editor.style.left = `${Math.max(0, Math.min(x, root.width - editor.offsetWidth))}px`
    editor.style.top = `${Math.max(viewport.top - root.top, Math.min(y, viewport.bottom - root.top - editor.offsetHeight))}px`
  }

  cancel() {
    if (this.saving) return
    this.editorTarget.hidden = true
    this.selected = null
  }

  async save(event) {
    event.preventDefault()
    if (this.saving || !this.selected) return
    const name = this.nameTarget.value.trim()
    if (!name) { this.errorTarget.textContent = "Bitte einen Namen eingeben."; return }
    const selected = this.selected
    const url = new URL(this.urlValue, window.location.origin)
    if (selected.id) url.pathname += `/${selected.id}`
    this.saving = true
    this.saveButtonTarget.disabled = true
    this.errorTarget.textContent = ""
    try {
      const response = await fetch(url, {
        method: selected.id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content },
        body: JSON.stringify({ annotation: { ...selected, name } })
      })
      const data = response.headers.get("content-type")?.includes("application/json") ? await response.json() : {}
      if (!response.ok) throw new Error(data.error || "Speichern nicht möglich. Bitte öffne Deinen Einladungslink erneut.")
      const index = this.items.findIndex(item => item.id === data.id)
      if (index < 0) this.items.push(data)
      else this.items[index] = data
      this.render()
      this.editorTarget.hidden = true
      this.selected = null
      this.statusTarget.textContent = `Gespeichert: ${data.name}`
    } catch (error) {
      this.errorTarget.textContent = error.message || "Verbindung fehlgeschlagen. Bitte versuche es erneut."
    } finally {
      this.saving = false
      this.saveButtonTarget.disabled = false
    }
  }
}
